import { useState, useCallback } from 'react';
import { Strings } from '@/constants/strings';
import { GenderSelection } from '@/components/GenderSelection';
import {
  calculateFindRiskScore,
  type FindRiskInput,
  type FindRiskGender,
  type FindRiskFamilyHistory,
  type FindRiskResult,
} from '@/data/FindRisk';
interface FindRiskCalculatorProps {
  initialBmi?: number | null;
  onResult: (result: FindRiskResult) => void;
}

export function FindRiskCalculator({ initialBmi, onResult }: FindRiskCalculatorProps) {
  const [gender, setGender] = useState<FindRiskGender | null>(null);
  const [age, setAge] = useState(45);
  const [bmi, setBmi] = useState(initialBmi != null ? String(initialBmi) : '');
  const [waist, setWaist] = useState('');
  const [physicalActivity, setPhysicalActivity] = useState<boolean | null>(null);
  const [vegetablesDaily, setVegetablesDaily] = useState<boolean | null>(null);
  const [bpMedication, setBpMedication] = useState<boolean | null>(null);
  const [highBloodGlucose, setHighBloodGlucose] = useState<boolean | null>(null);
  const [familyHistory, setFamilyHistory] = useState<FindRiskFamilyHistory | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const bmiNum = parseFloat(bmi.replace(',', '.')) || 0;
  const waistNum = parseFloat(waist.replace(',', '.')) || 0;

  const handleCalculate = useCallback(() => {
    const newErrors: Record<string, string> = {};

    if (gender === null) newErrors.gender = 'Cinsiyet seçin';
    if (age < 18 || age > 120) newErrors.age = 'Geçerli bir yaş girin';
    if (Number.isNaN(bmiNum) || bmiNum < 10 || bmiNum > 60)
      newErrors.bmi = 'Geçerli bir BMI girin (10-60)';
    if (Number.isNaN(waistNum) || waistNum < 50 || waistNum > 200)
      newErrors.waist = 'Geçerli bir bel çevresi girin (50-200 cm)';
    if (physicalActivity === null) newErrors.physicalActivity = 'Lütfen seçin';
    if (vegetablesDaily === null) newErrors.vegetablesDaily = 'Lütfen seçin';
    if (bpMedication === null) newErrors.bpMedication = 'Lütfen seçin';
    if (highBloodGlucose === null) newErrors.highBloodGlucose = 'Lütfen seçin';
    if (familyHistory === null) newErrors.familyHistory = 'Lütfen seçin';

    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    const input: FindRiskInput = {
      age,
      bmi: bmiNum,
      waistCm: waistNum,
      gender: gender!,
      physicalActivity: physicalActivity!,
      vegetablesDaily: vegetablesDaily!,
      bpMedication: bpMedication!,
      highBloodGlucose: highBloodGlucose!,
      familyHistory: familyHistory!,
    };
    const r = calculateFindRiskScore(input);
    onResult(r);
  }, [
    onResult,
    gender,
    age,
    bmiNum,
    waistNum,
    physicalActivity,
    vegetablesDaily,
    bpMedication,
    highBloodGlucose,
    familyHistory,
  ]);

  const RadioOption = ({
    name,
    selected,
    onSelect,
    label,
  }: {
    name: string;
    selected: boolean;
    onSelect: () => void;
    label: string;
  }) => (
    <label
      className={`flex min-h-[48px] cursor-pointer items-center gap-3 rounded-xl border-2 px-4 py-3 transition ${
        selected ? 'border-primary bg-primary/10' : 'border-slate-200 bg-card-bg'
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={selected}
        onChange={onSelect}
        className="size-5 shrink-0 border-2 border-gray-300 text-primary focus:ring-primary"
      />
      <span className="text-base text-text-heading">{label}</span>
    </label>
  );

  const ages = Array.from({ length: 83 }, (_, i) => i + 18);

  return (
    <div className="flex min-h-[calc(100vh-56px)] flex-1 flex-col bg-surface">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        <div className="mx-auto flex max-w-md flex-col space-y-6">
          <p className="text-center text-base text-text-muted">
            Aşağıdaki soruları yanıtlayın. 10 yıllık Tip 2 diyabet riskiniz hesaplanacaktır.
          </p>

          <div className="space-y-5">
            {/* Gender */}
            <div>
              <GenderSelection
                value={gender}
                onValueChange={(v) => setGender(v)}
                error={errors.gender}
              />
            </div>

            {/* Age */}
            <div>
              <label className="mb-2 block text-sm font-medium text-text-heading">
                {Strings.findRiskAgeLabel}
              </label>
              <select
                value={age}
                onChange={(e) => setAge(Number(e.target.value))}
                className="w-full min-h-[48px] rounded-xl border-2 border-slate-200 bg-card-bg px-4 py-3 text-base text-text-heading focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
              >
                {ages.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </div>

            {/* BMI */}
            <div>
              <label className="mb-2 block text-sm font-medium text-text-heading">
                {Strings.findRiskBmiLabel}
              </label>
              <input
                type="number"
                inputMode="decimal"
                min={10}
                max={60}
                step={0.1}
                value={bmi}
                onChange={(e) => setBmi(e.target.value)}
                placeholder={initialBmi != null ? undefined : 'örn: 24.5'}
                className="w-full min-h-[48px] rounded-xl border-2 border-slate-200 bg-card-bg px-4 py-3.5 text-base text-text-heading placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
              <p className="mt-1 text-xs text-[#5C5C5C]">{Strings.findRiskBmiHint}</p>
              {initialBmi != null && (
                <p className="mt-1 text-xs text-primary">BMI Hesaplama sonucunuz otomatik dolduruldu.</p>
              )}
              {errors.bmi && <p className="mt-1 text-xs text-red-600">{errors.bmi}</p>}
            </div>

            {/* Waist */}
            <div>
              <label className="mb-2 block text-sm font-medium text-text-heading">
                {Strings.findRiskWaistLabel}
              </label>
              <input
                type="number"
                inputMode="decimal"
                min={50}
                max={200}
                value={waist}
                onChange={(e) => setWaist(e.target.value)}
                placeholder="örn: 90"
                className="w-full min-h-[48px] rounded-xl border-2 border-slate-200 bg-card-bg px-4 py-3.5 text-base text-text-heading placeholder:text-slate-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition"
              />
              <p className="mt-1 text-xs text-[#5C5C5C]">{Strings.findRiskWaistHint}</p>
              {errors.waist && <p className="mt-1 text-xs text-red-600">{errors.waist}</p>}
            </div>

            {/* Physical Activity */}
            <div>
              <p className="mb-2 text-sm font-medium text-text-heading">
                {Strings.findRiskActivityLabel}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setPhysicalActivity(true)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    physicalActivity === true ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.yes}
                </button>
                <button
                  type="button"
                  onClick={() => setPhysicalActivity(false)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    physicalActivity === false ? 'border-black bg-black/35 font-bold text-gray-800' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.no}
                </button>
              </div>
              {errors.physicalActivity && (
                <p className="mt-1 text-xs text-red-600">{errors.physicalActivity}</p>
              )}
            </div>

            {/* Vegetables */}
            <div>
              <p className="mb-2 text-sm font-medium text-text-heading">
                {Strings.findRiskVegetablesLabel}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setVegetablesDaily(true)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    vegetablesDaily === true ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.yes}
                </button>
                <button
                  type="button"
                  onClick={() => setVegetablesDaily(false)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    vegetablesDaily === false ? 'border-black bg-black/35 font-bold text-gray-800' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.no}
                </button>
              </div>
              {errors.vegetablesDaily && (
                <p className="mt-1 text-xs text-red-600">{errors.vegetablesDaily}</p>
              )}
            </div>

            {/* BP Medication */}
            <div>
              <p className="mb-2 text-sm font-medium text-text-heading">
                {Strings.findRiskBpMedsLabel}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setBpMedication(true)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    bpMedication === true ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.yes}
                </button>
                <button
                  type="button"
                  onClick={() => setBpMedication(false)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    bpMedication === false ? 'border-black bg-black/35 font-bold text-gray-800' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.no}
                </button>
              </div>
              {errors.bpMedication && (
                <p className="mt-1 text-xs text-red-600">{errors.bpMedication}</p>
              )}
            </div>

            {/* High Blood Glucose */}
            <div>
              <p className="mb-2 text-sm font-medium text-text-heading">
                {Strings.findRiskHighGlucoseLabel}
              </p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setHighBloodGlucose(true)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    highBloodGlucose === true ? 'border-primary bg-primary/10 text-primary' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.yes}
                </button>
                <button
                  type="button"
                  onClick={() => setHighBloodGlucose(false)}
                  className={`flex-1 min-h-[48px] rounded-xl border-2 py-3.5 text-center text-base font-medium transition ${
                    highBloodGlucose === false ? 'border-black bg-black/35 font-bold text-gray-800' : 'border-slate-200 bg-card-bg text-text-muted'
                  }`}
                >
                  {Strings.no}
                </button>
              </div>
              {errors.highBloodGlucose && (
                <p className="mt-1 text-xs text-red-600">{errors.highBloodGlucose}</p>
              )}
            </div>

            {/* Family History */}
            <div>
              <p className="mb-2 text-sm font-medium text-text-heading">
                {Strings.findRiskFamilyLabel}
              </p>
              <div className="space-y-2">
                <RadioOption
                  name="family"
                  selected={familyHistory === 'none'}
                  onSelect={() => setFamilyHistory('none')}
                  label={Strings.findRiskFamilyNone}
                />
                <RadioOption
                  name="family"
                  selected={familyHistory === 'secondary'}
                  onSelect={() => setFamilyHistory('secondary')}
                  label={Strings.findRiskFamilySecondary}
                />
                <RadioOption
                  name="family"
                  selected={familyHistory === 'primary'}
                  onSelect={() => setFamilyHistory('primary')}
                  label={Strings.findRiskFamilyPrimary}
                />
              </div>
              {errors.familyHistory && (
                <p className="mt-1 text-xs text-red-600">{errors.familyHistory}</p>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={handleCalculate}
            className="w-full rounded-2xl bg-primary py-4 text-lg font-bold text-white transition hover:bg-primary-active active:bg-primary-active"
          >
            {Strings.calculate}
          </button>
        </div>
      </div>
    </div>
  );
}
